import React from "react";
import { Layers } from "lucide-react";
import Card from "./Card";

/**
 * §11j: the estate grouped by declared criticality. Three things live here and nothing else:
 * what each tier MEANS in this organization (org-authored guidance over built-in defaults),
 * what the org's response policy currently implies per tier (computed display — this tab
 * decides nothing), and the untiered backlog as a work queue with the classify control inline.
 */

const TIER_META = {
  1: {
    label: "Tier 1 — Most critical",
    tone: "border-purple-300 bg-purple-50 text-purple-900",
    fallback:
      "The assets this organization cannot afford to lose control of. By default, response " +
      "actions on them always require a second person's approval.",
  },
  2: {
    label: "Tier 2 — Standard",
    tone: "border-border bg-theme-hover text-content-primary",
    fallback: "The organization's response settings apply as configured.",
  },
  3: {
    label: "Tier 3 — Sandbox / experimental",
    tone: "border-border bg-theme-hover text-content-primary",
    fallback:
      "Low-blast-radius assets — experiments and sandboxes. Classification today; candidate " +
      "for relaxed handling in future rules.",
  },
};

// What the org's current settings mean for this tier's immediate response path. Display only —
// the server enforces; this sentence just saves the reader a trip to three toggles.
function policyLine(tier, config) {
  if (!config) return null;
  if (config.responseOverrideEnabled === false) {
    return "Immediate execution is off org-wide — every response action needs an approver.";
  }
  if (tier === 1) {
    return config.tier1ImmediateAllowed === true
      ? "May use the immediate response path — your organization waived Tier 1 approval (administrator-set policy)."
      : "Immediate response refused — containment requires a second person's approval.";
  }
  return "May use the immediate response path, with a mandatory written reason.";
}

// §11k: per-currency amounts, never merged.
const formatSpend = (amounts) => amounts
  .map((a) => (a.currency === "USD"
    ? `$${Math.round(a.amount).toLocaleString()}`
    : `${Math.round(a.amount).toLocaleString()} ${a.currency}`))
  .join(" + ");

export default function AiTiersTab({
  assets = [], config, canManage, savingTierId, onSaveTier, onOpenAsset, spendByAsset = {},
  spendUnavailable = false,
}) {
  const groups = [1, 2, 3].map((tier) => ({
    tier,
    meta: TIER_META[tier],
    assets: assets.filter((a) => a.tier === tier),
  }));
  const untiered = assets.filter((a) => a.tier == null);
  const guidance = (tier) =>
    config?.tierGuidance?.[String(tier)] || TIER_META[tier].fallback;

  const row = (asset) => (
    <div key={asset.id} className="flex items-center gap-3 px-4 py-2 border-t border-border">
      <button
        onClick={() => onOpenAsset?.(asset.id)}
        className="text-sm text-content-primary hover:text-purple-700 font-medium truncate text-left"
      >
        {asset.name}
      </button>
      <span className="text-xs text-content-secondary shrink-0">{asset.kind}</span>
      <span className="text-xs text-content-secondary shrink-0">{asset.governanceState}</span>
      {spendByAsset[asset.id]?.length > 0 && (
        <span
          className="text-[11px] font-medium text-emerald-800 shrink-0"
          title="Billed spend attributed to this asset over the last 30 days (complete days only — a floor)."
        >
          {formatSpend(spendByAsset[asset.id])} / 30d
        </span>
      )}
      <span className="ml-auto shrink-0">
        {canManage ? (
          <select
            value={asset.tier ?? ""}
            disabled={savingTierId === asset.id}
            onChange={(e) => onSaveTier(asset, e.target.value)}
            className="text-[11px] font-medium rounded px-1 py-0.5 border border-border bg-theme-primary text-content-secondary disabled:opacity-60"
          >
            <option value="">Untiered</option>
            <option value="1">Tier 1</option>
            <option value="2">Tier 2</option>
            <option value="3">Tier 3</option>
          </select>
        ) : (
          // AX-268: a read-only caller sees the tier, not a disabled control.
          <span className="text-[11px] font-medium text-content-secondary">
            {asset.tier ? `Tier ${asset.tier}` : "Untiered"}
          </span>
        )}
      </span>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* The irreversible policy is orthogonal to tiers, so it gets one line in BOTH states —
          the tier sentences below would otherwise imply "may execute immediately" covers
          actions like OpenAI key deletion when it does not (review finding). */}
      {config?.responseOverrideEnabled !== false && (
        config?.irreversibleImmediateAllowed === true ? (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            Irreversible actions (e.g. OpenAI project-key deletion) may currently execute
            immediately (administrator-set policy) — this applies at every tier.
          </div>
        ) : (
          <div className="text-xs text-content-secondary">
            Irreversible actions (e.g. OpenAI project-key deletion) require a second person's
            approval at every tier, whatever a tier's line below says about the immediate path.
          </div>
        )
      )}
      {spendUnavailable && (
        <div className="text-[11px] text-amber-700">
          Spend is unavailable right now — the ledger read failed, so rows show no figures.
          Blank does not mean zero.
        </div>
      )}

      {groups.map(({ tier, meta, assets: tierAssets }) => (
        <Card key={tier} className="overflow-hidden">
          <div className={`px-4 py-3 border-b ${meta.tone}`}>
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4" />
              <span className="font-semibold text-sm">{meta.label}</span>
              <span className="text-xs opacity-80">
                {tierAssets.length} asset{tierAssets.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="text-xs mt-1 opacity-90">{guidance(tier)}</div>
            <div className="text-[11px] mt-1 font-medium">{policyLine(tier, config)}</div>
          </div>
          {tierAssets.length === 0 ? (
            <div className="px-4 py-3 text-xs text-content-secondary">
              No assets declared Tier {tier}.
            </div>
          ) : (
            tierAssets.map(row)
          )}
        </Card>
      ))}

      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b border-amber-200 bg-amber-50 text-amber-900">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            <span className="font-semibold text-sm">Untiered</span>
            <span className="text-xs opacity-80">
              {untiered.length} asset{untiered.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="text-xs mt-1">
            Nobody has declared how critical these are — they behave exactly as if tiers did not
            exist. Criticality is declared by a risk manager, never inferred: classify them here.
          </div>
        </div>
        {untiered.length === 0 ? (
          <div className="px-4 py-3 text-xs text-content-secondary">
            Every discovered asset carries a tier.
          </div>
        ) : (
          untiered.map(row)
        )}
      </Card>
    </div>
  );
}
